import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
} from '@mui/material';

import { reportingUsageAPI } from '../services/reportingUsageApi';
import { PeriodPicker, Stat, StatGrid, Legend, BarChart, CustomerPicker, useCustomers, PALETTE, PERIODS, num, errText, monthShort, monthLong, typeLabel } from '../components/ReportingUsageParts';

const TYPE_COLOR = { P: PALETTE[0], B: PALETTE[1], L: PALETTE[2], Unknown: '#757575' };

// Sessions per month with the user type breakdown, for all customers and for one. Props: custCode (optional) opens a customer straight away.
export default function ReportingSessions({ custCode = '' }) {
  const [activeOnly, setActiveOnly] = useState(true);
  const { customers, error: listError } = useCustomers(activeOnly);
  const [selected, setSelected] = useState(custCode);
  const [dimension, setDimension] = useState('last_year');
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ by: 'total_sessions', dir: 'desc' });

  useEffect(() => { setSelected(custCode || ''); }, [custCode]);

  useEffect(() => {
    let live = true;
    setSummary(null);
    reportingUsageAPI.sessionsSummary(dimension)
      .then((res) => live && setSummary(res.data))
      .catch((err) => live && setError(errText(err, 'Could not load the sessions summary')));
    return () => { live = false; };
  }, [dimension]);

  useEffect(() => {
    if (!selected) { setData(null); return undefined; }
    let live = true;
    setLoading(true);
    reportingUsageAPI.customerSessions(selected, dimension)
      .then((res) => { if (live) { setData(res.data); setError(''); } })
      .catch((err) => { if (live) { setData(null); setError(errText(err, 'Could not load sessions')); } })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [selected, dimension]);

  const months = data?.months || [];
  const types = useMemo(() => [...new Set(months.flatMap((m) => Object.keys(m.by_type)))], [months]);
  const keys = types.map((t, i) => ({ key: t, label: typeLabel(t), color: TYPE_COLOR[t] || PALETTE[(i + 3) % PALETTE.length] }));
  const chart = months.map((m) => ({ label: monthShort(m.log_month), values: m.by_type }));

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const dir = sort.dir === 'asc' ? 1 : -1;
    const rows = (summary?.customers || []).filter((c) => !q || `${c.cust_code} ${c.cust_name} ${c.location || ''}`.toLowerCase().includes(q));
    const val = (c) => (sort.by === 'total_sessions' ? Number(c.total_sessions || 0) : String(c[sort.by] ?? '').toLowerCase());
    return rows.sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * dir);
  }, [summary, search, sort]);
  const sortBy = (by) => setSort((s) => ({ by, dir: s.by === by && s.dir === 'desc' ? 'asc' : 'desc' }));
  const head = (by, label, align) => (
    <TableCell align={align} sortDirection={sort.by === by ? sort.dir : false}>
      <TableSortLabel active={sort.by === by} direction={sort.by === by ? sort.dir : 'asc'} onClick={() => sortBy(by)}>{label}</TableSortLabel>
    </TableCell>
  );
  const busiest = summary?.customers?.[0];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Sessions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        User sessions recorded for each customer, by user type. Periods are whole months and end before the current month.
      </Typography>
      <Box sx={{ mb: 2 }}><PeriodPicker value={dimension} onChange={setDimension} /></Box>
      {(error || listError) && <Alert severity="error" sx={{ mb: 2 }}>{error || listError}</Alert>}
      {!summary && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
      {summary && (
        <StatGrid>
          <Stat label="Total sessions" value={num(summary.total_sessions)} sub={`all active customers, ${PERIODS[dimension].toLowerCase()}`} />
          <Stat label="Customers with sessions" value={num(summary.customer_count)} />
          <Stat label="Most active customer" value={busiest ? busiest.cust_name : '-'} sub={busiest ? `${num(busiest.total_sessions)} sessions` : ''} />
        </StatGrid>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2 }}><CustomerPicker customers={customers} value={selected} onChange={setSelected} activeOnly={activeOnly} onActiveOnly={setActiveOnly} /></Box>
        {!selected && <Typography color="text.secondary">Select a customer to see their session history.</Typography>}
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
        {data && !loading && (
          <>
            <StatGrid>
              <Stat label="Sessions" value={num(data.total)} sub={data.customer?.cust_name} />
              <Stat label="Months of data" value={num(data.month_count)} sub={data.date_from ? `${data.date_from} to ${data.date_to}` : 'all time'} />
              <Stat label="User types" value={num(types.length)} sub={types.map(typeLabel).join(', ')} />
            </StatGrid>
            {months.length === 0 ? <Alert severity="info">No sessions were recorded for this customer in this period.</Alert> : (
              <>
                <Typography variant="subtitle1" gutterBottom>Sessions per month</Typography>
                <Legend items={keys} />
                <BarChart data={chart} keys={keys} stacked ariaLabel="Sessions per month by user type" />
                <TableContainer sx={{ maxHeight: 420, mt: 2 }}>
                  <Table size="small" stickyHeader aria-label="Monthly breakdown">
                    <TableHead><TableRow><TableCell>Month</TableCell><TableCell align="right">Total</TableCell>{types.map((t) => <TableCell key={t} align="right">{typeLabel(t)}</TableCell>)}</TableRow></TableHead>
                    <TableBody>
                      {[...months].reverse().map((m) => (
                        <TableRow key={m.log_month} hover>
                          <TableCell>{monthLong(m.log_month)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>{num(m.total)}</TableCell>
                          {types.map((t) => <TableCell key={t} align="right">{num(m.by_type[t])}</TableCell>)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </Paper>

      {summary && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>All customers</Typography>
            <TextField size="small" label="Search customers" value={search} onChange={(e) => setSearch(e.target.value)} />
          </Box>
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader aria-label="All customers session summary">
              <TableHead><TableRow>{head('cust_name', 'Customer')}{head('location', 'Location')}{head('total_sessions', 'Sessions', 'right')}</TableRow></TableHead>
              <TableBody>
                {list.map((c) => (
                  <TableRow key={c.cust_code} hover selected={c.cust_code === selected} onClick={() => setSelected(c.cust_code)} sx={{ cursor: 'pointer' }}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{c.cust_name}</Typography><Typography variant="caption" color="text.secondary">{c.cust_code}</Typography></TableCell>
                    <TableCell>{c.location || '-'}</TableCell>
                    <TableCell align="right">{num(c.total_sessions)}</TableCell>
                  </TableRow>
                ))}
                {list.length === 0 && <TableRow><TableCell colSpan={3}>No customers have sessions in this period.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
